import { MANAGER_TASK_FREQUENCY, ManagerRecurringTaskTemplate, ManagerTask } from './types'

const BRATISLAVA_TIME_ZONE = 'Europe/Bratislava'

export const RECURRING_GHOST_TOOLTIP = 'Automaticky sa vygeneruje v daný deň.'

export interface RecurringGhostEvent {
	id: string
	templateId: number
	occurrenceDate: string
	occurrenceKey: string
	title: string
	timeWindowFrom?: string | null
	timeWindowTo?: string | null
	deadlineTime?: string | null
	tooltip: string
}

export interface BuildRecurringGhostEventsOptions {
	maxEventsPerDate?: number
}

// Cached at module level because Intl.DateTimeFormat instantiation is expensive.
const bratislavaFormatter = new Intl.DateTimeFormat('en-CA', {
	timeZone: BRATISLAVA_TIME_ZONE,
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	weekday: 'short'
})

const getBratislavaDateParts = (date: Date) => {
	const parts = bratislavaFormatter.formatToParts(date)

	const value = (type: string) => parts.find((part) => part.type === type)?.value || ''

	return {
		year: value('year'),
		month: value('month'),
		day: value('day'),
		weekday: value('weekday')
	}
}

type BratislavaDateParts = ReturnType<typeof getBratislavaDateParts>

const toOccurrenceDate = (parts: Pick<BratislavaDateParts, 'year' | 'month' | 'day'>) => `${parts.year}-${parts.month}-${parts.day}`

const buildBratislavaDateWindow = (date: Date, days: number) => {
	if (days < 0) {
		return []
	}

	const parts = getBratislavaDateParts(date)
	const startYear = Number(parts.year)
	const startMonth = Number(parts.month) - 1
	const startDay = Number(parts.day)
	const dates: Array<{ occurrenceDate: string; parts: BratislavaDateParts }> = []

	for (let offset = 0; offset <= days; offset += 1) {
		const nextParts = getBratislavaDateParts(new Date(Date.UTC(
			startYear,
			startMonth,
			startDay + offset,
			12,
			0,
			0
		)))
		dates.push({
			occurrenceDate: toOccurrenceDate(nextParts),
			parts: nextParts
		})
	}

	return dates
}

// Maps Intl weekday abbreviations ('Mon', 'Tue', ...) to DAY_KEYS ('MON', 'TUE', ...)
const INTL_WEEKDAY_TO_KEY: Record<string, string> = {
	Sun: 'SUN', Mon: 'MON', Tue: 'TUE', Wed: 'WED', Thu: 'THU', Fri: 'FRI', Sat: 'SAT'
}

export const parseWeekDays = (raw: string | null | undefined): string[] => {
	if (!raw) return []
	if (raw.startsWith('[')) {
		try { return JSON.parse(raw) } catch { return [] }
	}
	return raw.split(',').map((d) => d.trim()).filter(Boolean)
}

const shouldShowOccurrence = (template: ManagerRecurringTaskTemplate, parts: BratislavaDateParts) => {
	if (!template.isActive) {
		return false
	}

	const isWeekend = parts.weekday === 'Sat' || parts.weekday === 'Sun'
	if (template.workingDaysOnly && isWeekend) {
		return false
	}

	if (template.frequency === MANAGER_TASK_FREQUENCY.WEEKLY) {
		const weekDays = parseWeekDays(template.weekDays)
		if (weekDays.length > 0) {
			const todayKey = INTL_WEEKDAY_TO_KEY[parts.weekday] || ''
			return weekDays.includes(todayKey)
		}
		// backward-compatible fallback: Monday
		return parts.weekday === 'Mon'
	}

	if (template.frequency === MANAGER_TASK_FREQUENCY.MONTHLY) {
		if (template.monthDay != null) {
			return Number(parts.day) === template.monthDay
		}
		// backward-compatible fallback: day 1
		return parts.day === '01'
	}

	return template.frequency === MANAGER_TASK_FREQUENCY.DAILY
}

export const buildRecurringGhostEvents = (
	templates: ManagerRecurringTaskTemplate[],
	now: Date,
	days = 30,
	existingTasks: Pick<ManagerTask, 'occurrenceKey' | 'recurringTemplateId' | 'occurrenceDate'>[] = [],
	options: BuildRecurringGhostEventsOptions = {}
): RecurringGhostEvent[] => {
	const existingOccurrenceKeys = new Set(
		existingTasks
			.map((task) => task.occurrenceKey || (task.recurringTemplateId && task.occurrenceDate ? `${task.recurringTemplateId}:${task.occurrenceDate}` : null))
			.filter(Boolean) as string[]
	)
	const dateWindow = buildBratislavaDateWindow(now, days)
	const maxEventsPerDate = options.maxEventsPerDate && options.maxEventsPerDate > 0
		? Math.floor(options.maxEventsPerDate)
		: undefined
	const eventCountsByDate = new Map<string, number>()
	const events: RecurringGhostEvent[] = []
	let cappedDateCount = 0

	templates.forEach((template) => {
		if (maxEventsPerDate && cappedDateCount >= dateWindow.length) {
			return
		}

		dateWindow.forEach(({ occurrenceDate, parts }) => {
			const currentCount = eventCountsByDate.get(occurrenceDate) || 0
			if (maxEventsPerDate && currentCount >= maxEventsPerDate) {
				return
			}

			if (shouldShowOccurrence(template, parts)) {
				const occurrenceKey = `${template.id}:${occurrenceDate}`
				if (!existingOccurrenceKeys.has(occurrenceKey)) {
					events.push({
						id: `ghost-${occurrenceKey}`,
						templateId: template.id,
						occurrenceDate,
						occurrenceKey,
						title: template.title,
						timeWindowFrom: template.timeWindowFrom,
						timeWindowTo: template.timeWindowTo,
						deadlineTime: template.deadlineTime,
						tooltip: RECURRING_GHOST_TOOLTIP
					})
					const nextCount = currentCount + 1
					eventCountsByDate.set(occurrenceDate, nextCount)
					if (maxEventsPerDate && nextCount === maxEventsPerDate) {
						cappedDateCount += 1
					}
				}
			}
		})
	})

	return events.sort((a, b) => a.occurrenceDate.localeCompare(b.occurrenceDate))
}
